import {
  type EditedWorld,
  footprintOf,
  type Footprint,
  type PropPlacement,
  type WorldDen,
} from '@shefira/shared';

/**
 * What the world editor has in hand (ADR 0022), and where it stands in the
 * edited world. A prop and an inhabitant go by their origin (see
 * `EditModel`), a portal and a chest by their id.
 */
export type Selection =
  | { kind: 'prop'; origin: string }
  | { kind: 'spawn'; origin: string }
  | { kind: 'portal'; id: string }
  | { kind: 'chest'; id: string };

export function selectionKey(selection: Selection): string {
  return `${selection.kind}:${selection.kind === 'prop' || selection.kind === 'spawn' ? selection.origin : selection.id}`;
}

export function sameSelection(a: Selection | undefined, b: Selection | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return selectionKey(a) === selectionKey(b);
}

export function propOf(world: EditedWorld, origin: string): PropPlacement | undefined {
  return world.props.find((prop) => prop.origin === origin)?.placement;
}

export function spawnOf(world: EditedWorld, origin: string): WorldDen | undefined {
  return world.spawns.find((spawn) => spawn.origin === origin)?.den;
}

export function positionOf(
  world: EditedWorld,
  selection: Selection,
): { x: number; y: number } | undefined {
  switch (selection.kind) {
    case 'prop':
      return propOf(world, selection.origin);
    case 'spawn':
      return spawnOf(world, selection.origin);
    case 'portal':
      return world.layout.portals.find((portal) => portal.id === selection.id);
    case 'chest':
      return world.layout.chests.find((chest) => chest.id === selection.id);
  }
}

/** The tiles it covers, for its outline. */
export function boxOf(world: EditedWorld, selection: Selection): Footprint | undefined {
  if (selection.kind === 'prop') {
    const placed = propOf(world, selection.origin);
    return placed === undefined ? undefined : footprintOf(placed);
  }
  const at = positionOf(world, selection);
  if (at === undefined) return undefined;
  if (selection.kind === 'spawn') return { x0: at.x, y0: at.y, x1: at.x, y1: at.y };
  return footprintOf({ prop: selection.kind, ...at });
}

/** One line for the panel. */
export function describe(world: EditedWorld, selection: Selection): string {
  const at = positionOf(world, selection);
  const where = at === undefined ? '' : ` (${String(at.x)}, ${String(at.y)})`;
  switch (selection.kind) {
    case 'prop': {
      const placed = propOf(world, selection.origin);
      const mirrored = placed?.flip === true ? ', miroir' : '';
      const added = selection.origin.startsWith('added:') ? ', ajouté' : '';
      return `Décor ${placed?.prop ?? '?'}${where}${mirrored}${added}`;
    }
    case 'spawn': {
      const den = spawnOf(world, selection.origin);
      const facing = den?.facing === undefined ? '' : `, tourné ${den.facing}`;
      const added = selection.origin.startsWith('added-spawn:') ? ', ajouté' : '';
      return `Habitant ${den?.mob ?? '?'} ×${String(den?.count ?? 0)}${where}${facing}${added}`;
    }
    case 'portal':
      return `Portail ${selection.id}${where}`;
    case 'chest':
      return `Coffre ${selection.id}${where}`;
  }
}
